import { APP_BASE_HREF } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject, Input, Optional } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, map } from 'rxjs';
import { BROWSER_TITLE_FALLBACK_VALUE, TitlePartString } from '@capital-access/common/browser';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import {
  getAccountPreferences,
  getUserLoadStatus,
  REPORT_ACCESS_ONLY_PREFERENCE_KEY,
  REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE
} from '@capital-access/common/user';
import { FORBIDDEN_PAGE_ROUTE, INTERNAL_SERVER_ERROR_PAGE_ROUTE, LoadStatus } from '@capital-access/common/utils';
import { SideNavService } from '../side-nav.service';

@Component({
  selector: 'ca-logo',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./logo.component.scss'],
  templateUrl: './logo.component.html',
  standalone: false
})
export class LogoComponent {
  @Input() plainView = false;
  @Input() appTitle = '';
  @Input() titleCssClass = '';
  @Input() showTitle = true;

  private destroyRef = inject(DestroyRef);

  icon$ = this.sideNavService.panelExpanded$.pipe(map(expanded => !expanded));

  constructor(
    private router: Router,
    private sideNavService: SideNavService,
    private store: Store,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(FORBIDDEN_PAGE_ROUTE) private forbiddenPageRoute: string,
    @Inject(INTERNAL_SERVER_ERROR_PAGE_ROUTE) private internalErrorPageRoute: string,
    @Optional() @Inject(BROWSER_TITLE_FALLBACK_VALUE) public title: TitlePartString
  ) {}

  toggleButton() {
    this.sideNavService.togglePanelExpanded();
  }

  navigateHome() {
    // Force navigation due to issue https://teamipreo.atlassian.net/browse/CPD-1746
    const match = [this.forbiddenPageRoute, this.internalErrorPageRoute].some(url => location.pathname.includes(url));
    if (match) {
      location.assign(location.origin + (this.baseHref ?? '/'));
    } else {
      // Check if user has report access restriction and navigate accordingly
      const userStatus$ = this.store.select(getUserLoadStatus);
      const preferences$ = this.store.select(getAccountPreferences);
      const featureToggle$ = this.store.select(getFeatureToggle(REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE));

      combineLatest([userStatus$, preferences$, featureToggle$])
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(([status, preferences, isFeatureEnabled]) => {
            if (status !== LoadStatus.Loaded || !isFeatureEnabled || !preferences || !Array.isArray(preferences)) {
              return '/';
            }

            // Check if user has report-access-only preference set to true
            const hasReportAccessOnly = preferences.some(
              pref => pref.key === REPORT_ACCESS_ONLY_PREFERENCE_KEY && pref.value === 'true'
            );

            return hasReportAccessOnly ? '/reporting' : '/';
          })
        )
        .subscribe(route => {
          this.router.navigate([route]);
        });
    }
  }
}
